import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { fetchPatientsWithCylinders } from '../../../services/recharge/rechargeService';
import { isGenericCylinder } from '../../../utils/orders';

const PATIENTS_CACHE_TTL_MS = 30 * 1000;
const MAX_PAGE_SIZE = 50;
const patientsCache = new Map();
const inFlightPatients = new Map();
let fetchPatientsSeq = 0;

const makePatientsCacheKey = ({ filter, onlyWithCylinders, page, pageSize }) =>
  JSON.stringify({
    filter: filter ?? null,
    onlyWithCylinders: !!onlyWithCylinders,
    page: Number(page ?? 0),
    pageSize: Number(pageSize ?? 10),
  });

const getDefaultRechargeAction = (cylinder) => {
  if (cylinder?.selectionMode === 'exchange') return 'troca';
  if (isGenericCylinder(cylinder)) return 'troca';
  return 'recarga';
};

const clampCylinderQuantity = (cylinder, quantity) => {
  if (quantity === null || quantity === undefined || quantity === '')
    return null;

  const parsedQuantity = Number.parseInt(String(quantity), 10);
  if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) return null;

  return parsedQuantity;
};

export const useRechargeStore = create(
  persist(
    (set, get) => ({
      selectedPatient: null,
      patientInfoMessage: null,
      patients: [],
      patientsLoading: false,
      currentFilter: null,
      rechargerActions: {},
      exChangeCylinders: {},
      expandVisible: {},
      onlyWithCylinders: false,
      pageSize: 10,
      page: 0,
      rows: 0,
      selectedPatientIndex: -1,
      listState: [],
      setListState: (l) => {
        set({ listState: [...l] });
      },
      setPagination: (page, pageSize) => {
        const parsedPage = Number.parseInt(String(page ?? 0), 10);
        const parsedPageSize = Number.parseInt(String(pageSize ?? 10), 10);

        set({
          page: Number.isFinite(parsedPage) ? parsedPage : 0,
          pageSize: Number.isFinite(parsedPageSize)
            ? Math.min(MAX_PAGE_SIZE, Math.max(1, parsedPageSize))
            : 10,
        });
      },
      setOnlyWithCylinder: (value) => {
        set({ onlyWithCylinders: value });
      },
      setPatientInfoMessage: (patientInfoMessage) => {
        set({ patientInfoMessage });
      },
      clearPatientsCache: () => {
        patientsCache.clear();
        inFlightPatients.clear();
      },

      fetchPatients: async (filter) => {
        const currentFilter = get().currentFilter ?? null;
        const normalizedFilter =
          filter === undefined ? currentFilter : filter ?? null;
        const hasExplicitFilter = String(normalizedFilter ?? '').trim() !== '';

        const previousFilter = currentFilter;
        let page = get().page;
        const storedPageSize = Number(get().pageSize);
        const pageSize = Number.isFinite(storedPageSize)
          ? Math.min(MAX_PAGE_SIZE, Math.max(1, storedPageSize))
          : 10;
        if (pageSize !== get().pageSize) {
          set({ pageSize });
        }
        if (normalizedFilter !== previousFilter) {
          page = 0;
          set({ page: 0, currentFilter: normalizedFilter });
        }

        const key = makePatientsCacheKey({
          filter: normalizedFilter,
          onlyWithCylinders: get().onlyWithCylinders,
          page,
          pageSize,
        });

        const cached = patientsCache.get(key);
        if (!hasExplicitFilter && cached && cached.expiresAt > Date.now()) {
          set({
            rows: cached.rows,
            patients: [...cached.patients],
            patientInfoMessage: null,
            rechargerActions: JSON.parse(
              JSON.stringify(cached.rechargerActions)
            ),
            expandVisible: JSON.parse(JSON.stringify(cached.expandVisible)),
          });
          return { rows: cached.rows, data: cached.patients };
        }

        const existing = inFlightPatients.get(key);
        if (existing) {
          return await existing;
        }

        const mySeq = ++fetchPatientsSeq;
        set({ patientsLoading: true, exChangeCylinders: {} });

        const promise = (async () => {
          try {
            const patients = await fetchPatientsWithCylinders(
              normalizedFilter,
              get().onlyWithCylinders,
              page,
              pageSize,
              'Ativo'
            );

            const obj = {};
            const dd = {};
            patients.data?.forEach((p, idx) => {
              obj[idx] = Array.from({ length: p.cylinders.length * 2 }).map(
                () => false
              );
              p.cylinders.forEach((cylinder, idxCylinder) => {
                if (!dd[idx]) dd[idx] = {};
                if (!dd[idx][idxCylinder]) dd[idx][idxCylinder] = {};

                dd[idx][idxCylinder].cylinder = { ...cylinder };
                dd[idx][idxCylinder].action =
                  getDefaultRechargeAction(cylinder);
                dd[idx][idxCylinder].quantity = null;
              });
            });

            // Se uma requisição mais nova foi disparada, ignora esta resposta.
            if (mySeq !== fetchPatientsSeq) {
              return patients;
            }

            set({
              rows: patients.rows,
              patients: [...patients.data],
              patientInfoMessage: null,
              rechargerActions: JSON.parse(JSON.stringify(dd)),
              expandVisible: JSON.parse(JSON.stringify(obj)),
            });

            if (!hasExplicitFilter) {
              patientsCache.set(key, {
                expiresAt: Date.now() + PATIENTS_CACHE_TTL_MS,
                rows: patients.rows,
                patients: [...patients.data],
                rechargerActions: dd,
                expandVisible: obj,
              });
            } else {
              patientsCache.delete(key);
            }

            return patients;
          } finally {
            inFlightPatients.delete(key);
            // Só limpa loading se essa ainda é a última chamada
            if (mySeq === fetchPatientsSeq) {
              set({ patientsLoading: false });
            }
          }
        })();

        inFlightPatients.set(key, promise);
        return await promise;
      },

      setExchangeCylinders: (obj) => {
        set({ exChangeCylinders: JSON.parse(JSON.stringify(obj)) });
      },
      setExpandVisible: (obj) => {
        set({ expandVisible: JSON.parse(JSON.stringify(obj)) });
      },

      setSelectedPatient: (patient) => {
        if (patient === null) {
          set({ selectedPatient: null });
        } else {
          set({ selectedPatient: { ...patient } });
        }
      },
      setReChargerActions: (idx, idxCylinder, cylinder, action) => {
        const dd = get().rechargerActions;
        if (!dd[idx]) {
          dd[idx] = {};
        }
        const previous = dd[idx][idxCylinder] ?? {};
        const previousQuantity = previous?.quantity;
        const previousAction = previous?.action;

        dd[idx][idxCylinder] = {
          cylinder: { ...cylinder },
          action,
          quantity:
            previousQuantity === undefined
              ? null
              : clampCylinderQuantity(cylinder, previousQuantity),
        };

        if (action === 'troca') {
          if (previousAction === 'troca') {
            if (previous?.newCylinder) {
              dd[idx][idxCylinder].newCylinder = { ...previous.newCylinder };
            }
            if (previous?.newCylinderIdx !== undefined) {
              dd[idx][idxCylinder].newCylinderIdx = previous.newCylinderIdx;
            }
          }
        }
        set({
          rechargerActions: JSON.parse(JSON.stringify(dd)),
          selectedPatientIndex: idx,
        });
      },
      setReChargerActionsQuantity: (idx, idxCylinder, cylinder, quantity) => {
        const dd = get().rechargerActions;
        if (!dd[idx]) {
          dd[idx] = {};
        }
        if (!dd[idx][idxCylinder]) {
          dd[idx][idxCylinder] = {};
        }
        dd[idx][idxCylinder].cylinder = { ...cylinder };
        dd[idx][idxCylinder].quantity = clampCylinderQuantity(
          cylinder,
          quantity
        );
        set({
          rechargerActions: JSON.parse(JSON.stringify(dd)),
          selectedPatientIndex: idx,
        });
      },
      setReChargerExchangeActions: (
        idx,
        idxCylinder,
        newCylinder,
        newCylinderIdx
      ) => {
        const dd = get().rechargerActions;
        if (!dd[idx]) {
          return;
        }

        if (!dd[idx][idxCylinder]) {
          dd[idx][idxCylinder] = {};
        }

        dd[idx][idxCylinder].newCylinder = { ...newCylinder };
        dd[idx][idxCylinder].newCylinderIdx = newCylinderIdx;
        dd[idx][idxCylinder].action = 'troca';

        set({
          rechargerActions: JSON.parse(JSON.stringify(dd)),
          selectedPatientIndex: idx,
        });
      },
      reset: () => {
        const clone = JSON.parse(JSON.stringify(get().expandVisible));
        Object.keys(clone).forEach((k) => {
          clone[k] = false;
        });
        set({
          listState: [],
          selectedPatient: null,
          patientInfoMessage: null,
          selectedPatientIndex: -1,
          rechargerActions: {},
          exChangeCylinders: {},
          expandVisible: clone,
          onlyWithCylinders: false,
          patients: [],
          patientsLoading: false,
        });
      },
    }),
    {
      name: 'recharge-store',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        selectedPatient: state.selectedPatient,
        rechargerActions: state.rechargerActions,
        exChangeCylinders: state.exChangeCylinders,
        expandVisible: state.expandVisible,
        selectedPatientIndex: state.selectedPatientIndex,
        currentFilter: state.currentFilter,
        onlyWithCylinders: state.onlyWithCylinders,
        pageSize: state.pageSize,
        page: state.page,
        rows: state.rows,
        listState: state.listState,
        patients: state.patients,
      }),
    }
  )
);
